import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';


const Goal = () => {
    const navigate = useNavigate();

    // =========================================================================
    // CONFIGURACIÓN DEL JUEGO
    // =========================================================================
    const TOTAL_DURATION_SECONDS = 20 //30; // Duración de la carrera
    const KEY_P1 = 'q'; // Tecla Jugador 1
    const KEY_P2 = 'p'; // Tecla Jugador 2
    const MAX_TAPS_PER_SECOND = 12; // Antitrampas: Límite de pulsaciones por segundo
    
    // =========================================================================
    // ESTADO DEL JUEGO
    // =========================================================================
    const [phase, setPhase] = useState('IDLE'); 
    const [countdown, setCountdown] = useState(3);
    const [viewStage, setViewStage] = useState('RACING'); 
    const [winner, setWinner] = useState(null);

    // Tiempo en milisegundos para el motor
    const [elapsedMs, setElapsedMs] = useState(0);
    const [displayTaps, setDisplayTaps] = useState({ p1: 0, p2: 0 });

    // Memoria silenciosa (Ref)
    const p1TapsRef = useRef(0);
    const p2TapsRef = useRef(0);

    // =========================================================================
    // ANTITRAMPAS (Rate Limiting Local)
    // =========================================================================
    const lastTapTimesP1 = useRef([]);
    const lastTapTimesP2 = useRef([]);
    const maxPositionsHistoryRef = useRef({ zona1: 0, zona2: 0 });

    const isValidTap = (playerRefArray) => {
        const now = Date.now();
        playerRefArray.current = playerRefArray.current.filter(time => now - time < 1000);
        
        if (playerRefArray.current.length >= MAX_TAPS_PER_SECOND) {
            return false; 
        }
        
        playerRefArray.current.push(now);
        return true;
    };

    // =========================================================================
    // MOTOR DEL JUEGO
    // =========================================================================
    
    // 1. Escuchar el Teclado
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (phase !== 'RUNNING' || e.repeat) return;
            
            const key = e.key.toLowerCase();
            
            if (key === KEY_P1) {
                if (isValidTap(lastTapTimesP1)) p1TapsRef.current++;
            } else if (key === KEY_P2) {
                if (isValidTap(lastTapTimesP2)) p2TapsRef.current++;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [phase]);

    // 2. Controlar la Cuenta Regresiva Inicial
    useEffect(() => {
        if (phase === 'COUNTDOWN') {
            if (countdown > 0) {
                const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
                return () => clearTimeout(timer);
            } else {
                setPhase('RUNNING');
            }
        }
    }, [phase, countdown]);

    // 3. Ciclo de Juego (Reloj y validación)
    useEffect(() => {
        let gameLoop;

        if (phase === 'RUNNING') {
            const totalDurationMs = TOTAL_DURATION_SECONDS * 1000;
            const startTime = Date.now() - elapsedMs;

            gameLoop = setInterval(() => {
                const now = Date.now();
                const currentElapsed = now - startTime;

                if (currentElapsed >= totalDurationMs) {
                    setElapsedMs(totalDurationMs);
                    setPhase('FINISHED');
                    
                    if (p1TapsRef.current > p2TapsRef.current) setWinner('zona1');
                    else if (p2TapsRef.current > p1TapsRef.current) setWinner('zona2');
                    else setWinner('tie');
                    
                } else {
                    setElapsedMs(currentElapsed);
                    setDisplayTaps({ p1: p1TapsRef.current, p2: p2TapsRef.current });
                }
            }, 100);
        }

        return () => clearInterval(gameLoop);
    }, [phase, elapsedMs]);

    // 4. Controlar Animaciones Finales
    useEffect(() => {
        if (phase === 'FINISHED') {
            if (winner === 'zona1' || winner === 'zona2') {
                setViewStage('KICK_ANIMATION');
                setTimeout(() => setViewStage('WINNER_TEXT'), 2000);
            } else {
                setViewStage('WINNER_TEXT');
            }
        }
    }, [phase, winner]);

    // =========================================================================
    // CÁLCULOS MATEMÁTICOS PARA EL AVANCE
    // =========================================================================
    const timeLeft = Math.ceil(TOTAL_DURATION_SECONDS - (elapsedMs / 1000));
    const timeCompletedPercentage = (elapsedMs / (TOTAL_DURATION_SECONDS * 1000)) * 100;
    
    const highestTapsOverall = Math.max(displayTaps.p1, displayTaps.p2);
    const MAXIMUM_VISUAL_LAG_PERCENTAGE = 25;

    const getFinalPosition = (zoneId, playerTaps) => {
        let finalPosition = timeCompletedPercentage;

        if (highestTapsOverall > 0) {
            const tapsDeficit = highestTapsOverall - playerTaps;
            const lagRatio = tapsDeficit / highestTapsOverall;
            finalPosition -= (lagRatio * MAXIMUM_VISUAL_LAG_PERCENTAGE);
        }

        if (phase === 'IDLE' || phase === 'COUNTDOWN') {
            finalPosition = 0;
        }

        return Math.min(100, Math.max(0, finalPosition));
    };

    // =========================================================================
    // AYUDANTES VISUALES
    // =========================================================================
    const ZONES_SKINS = {
        'zona1': { avatar: '/img/games/runtogoal/redbull/xolo-rojo.png', spark: '/img/games/runtogoal/redbull/destellos-rojos.png' },
        'zona2': { avatar: '/img/games/runtogoal/redbull/xolo-blanco.png', spark: '/img/games/runtogoal/redbull/destellos-blancos.png' }
    };

    const getWinnerImage = (winnerRaw) => {
        if (winnerRaw === 'zona2') return "/img/games/runtogoal/redbull/xolo-blanco-meta.png";
        return "/img/games/runtogoal/redbull/xolo-rojo-meta.png";
    };

    const renderWinnerTextScreen = (winnerRaw) => {
        if (winnerRaw === 'tie') return '¡ES UN EMPATE!';
        if (winnerRaw === 'zona1') return '¡HA GANADO EL JUGADOR 1!';
        if (winnerRaw === 'zona2') return '¡HA GANADO EL JUGADOR 2!';
        return '¡CARRERA FINALIZADA!';
    };

    // =========================================================================
    // RENDERIZADO
    // =========================================================================
    return (
        <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden', backgroundColor: '#111' }}>
            
            {/* PANTALLA DE INICIO */}
            {phase === 'IDLE' && (
                <div style={{ position: 'absolute', zIndex: 100, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                    <img src="/img/page/xolos-five.png" style={{ width: '100%', maxWidth: '400px' }} alt="Sponsor" />
                    <h1 style={{ fontSize: '4rem', fontWeight: 'bold', marginBottom: '10px' }}>CARRERA 1 VS 1</h1>
                    <p style={{ fontSize: '1.5rem', marginBottom: '40px' }}>Usa tu teclado para correr</p>
                    
                    <div style={{ display: 'flex', gap: '50px', marginBottom: '40px' }}>
                        <div style={{ textAlign: 'center', backgroundColor: '#e53935', padding: '20px 40px', borderRadius: '15px' }}>
                            <h3>Jugador 1</h3>
                            <p>Presiona la tecla</p>
                            <p style={{ fontSize: '5rem', margin: 0 }}>Q</p>
                        </div>
                        <div style={{ textAlign: 'center', backgroundColor: '#1e88e5', padding: '20px 40px', borderRadius: '15px' }}>
                            <h3>Jugador 2</h3>
                            <p>Presiona la tecla</p>
                            <p style={{ fontSize: '5rem', margin: 0 }}>P</p>
                        </div>
                    </div>

                    <button 
                        className="btn btn-success btn-lg" 
                        style={{ fontSize: '2rem', padding: '15px 50px', borderRadius: '50px' }}
                        onClick={() => setPhase('COUNTDOWN')}
                    >
                        ¡Iniciar!
                    </button>
                    
                    <button className="btn btn-outline-light mt-4" onClick={() => navigate('/lobby')}>
                        Volver al menú
                    </button>
                </div>
            )}

            {/* CUENTA REGRESIVA */}
            {phase === 'COUNTDOWN' && (
                <div style={{ position: 'absolute', zIndex: 100, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <p style={{ fontSize: '15rem', color: 'white', fontWeight: 'bold' }}>
                        {countdown > 0 ? countdown : '¡YA!'}
                    </p>
                </div>
            )}

            {/* RELOJ EN PANTALLA DURANTE LA CARRERA */}
            {(phase === 'RUNNING' || phase === 'FINISHED') && (
                <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 10, backgroundColor: 'rgba(0,0,0,0.7)', color: 'white', padding: '10px 30px', borderRadius: '10px', fontSize: '3rem', fontWeight: 'bold', fontFamily: 'monospace' }}>
                    00:{timeLeft < 10 ? `0${timeLeft}` : timeLeft}
                </div>
            )}

            {/* LA PISTA VISUAL */}
            <div className='GoalScreenX__Sponsor'> {/* style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '6px 0', marginTop: '12px', position: 'relative', zIndex: 5 }} */}
                <img src="/img/page/xolos-five-black.png" style={{ width: '100%', maxWidth: '280px', backgroundColor: 'rgba(255,255,255,0.8)', borderRadius: '8px', padding: '6px 0', }} alt="Sponsor" />
            </div>

            <div className="goal">
                <div className="goal__pitch Goal__V2__Background"></div>

                <div className="goal__lanes" style={{ gridTemplateRows: `repeat(2, 1fr)` }}>
                    {/* CARRIL 1 */}
                    <div className="goal__lane" style={{ '--finish-gap': `20vw` }}>
                        <div className="goal__runner" style={{ '--pct': getFinalPosition('zona1', displayTaps.p1) / 100 }}>
                            <img className="goal__velocity" style={{ width: '150px' }} src={ZONES_SKINS.zona1.spark} alt="destellos" />
                            <img className="goal__avatar" src={ZONES_SKINS.zona1.avatar} alt="Jugador 1" />
                        </div>
                    </div>

                    {/* CARRIL 2 */}
                    <div className="goal__lane" style={{ '--finish-gap': `17vw` }}>
                        <div className="goal__runner" style={{ '--pct': getFinalPosition('zona2', displayTaps.p2) / 100 }}>
                            <img className="goal__velocity" style={{ width: '150px' }} src={ZONES_SKINS.zona2.spark} alt="destellos" />
                            <img className="goal__avatar" src={ZONES_SKINS.zona2.avatar} alt="Jugador 2" />
                        </div>
                    </div>
                </div>

                {/* ANIMACIONES FINALES */}
                {viewStage === 'KICK_ANIMATION' && (
                    <div className="goal-outro">
                        <img src={getWinnerImage(winner)} className="goal-outro__ball" alt="Ganador" />
                    </div>
                )}

                {viewStage === 'WINNER_TEXT' && (
                    <div className="goal__pov">
                        <div className="goal__povGoool text-center">
                            {renderWinnerTextScreen(winner)}
                        </div>
                        <div className="goal__fw">
                            <div className="goal__fwBurst" />
                            <div className="goal__fwBurst goal__fwBurst--b2" />
                            <div className="goal__fwBurst goal__fwBurst--b3" />
                        </div>

                        {/* BOTONES POST-JUEGO CORREGIDOS */}
                        {/* <div style={{ position: 'absolute', bottom: '10%', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '20px', zIndex: 50 }}>
                            <button className="btn btn-warning btn-lg fw-bold" onClick={() => {
                                // Reiniciamos el motor y los estados visuales
                                p1TapsRef.current = 0;
                                p2TapsRef.current = 0;
                                setDisplayTaps({ p1: 0, p2: 0 });
                                setElapsedMs(0);
                                setCountdown(3);
                                setViewStage('RACING');
                                setWinner(null);
                                setPhase('IDLE');
                            }}>Jugar de nuevo</button>
                            <button className="btn btn-outline-light btn-lg" onClick={() => navigate('/lobby')}>Volver al menú</button>
                        </div> */}

                        {/* BOTONES POST-JUEGO CORREGIDOS */}
                        <div style={{ 
                            position: 'absolute', 
                            bottom: '10%', 
                            left: '50%', 
                            transform: 'translateX(-50%)', 
                            display: 'flex', 
                            gap: '20px', 
                            zIndex: 9999, /* Lo subimos al máximo */
                            pointerEvents: 'auto' /* EL ANTÍDOTO: Forzamos a que acepte clics */
                        }}>
                            <button className="btn btn-warning btn-lg fw-bold" onClick={() => {
                                // Reiniciamos el motor y los estados visuales
                                p1TapsRef.current = 0;
                                p2TapsRef.current = 0;
                                setDisplayTaps({ p1: 0, p2: 0 });
                                setElapsedMs(0);
                                setCountdown(3);
                                setViewStage('RACING');
                                setWinner(null);
                                setPhase('IDLE');
                            }}>
                                <i className="bi bi-arrow-counterclockwise me-2"></i>Jugar de nuevo
                            </button>
                            <button className="btn btn-outline-light btn-lg" onClick={() => navigate('/ztudia/lobby')}>
                                <i className="bi bi-house-door-fill me-2"></i>Volver al menú
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <footer className='goal__footer' style={{ position: 'fixed', bottom: 0, width: '100%', zIndex: 90 }}>
                <p className='text-center FooterCredits' style={{ color: 'white'}}> {/* style={{ color: 'white', backgroundColor: 'rgba(0,0,0,0.5)', margin: 0, padding: '5px' }} */}
                    Hecho con ♥ por Interaktia | 2026
                </p>
            </footer>
        </div>
    );
};

export { Goal };